import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Building2, User, Mail, Phone, MapPin, Lock, ArrowRight, Check, Smartphone, Copy } from 'lucide-react';
import api from '../lib/api';

const AgencyRegister = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    agency_name: '', email: '', phone: '', address: '', city: 'Bamako',
    admin_name: '', admin_password: '', confirm_password: '', plan: 'monthly'
  });
  const [result, setResult] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get('/subscription-plans');
        setPlans(res.data);
      } catch {
        setPlans([
          { id: 'monthly', name: 'Mensuel', amount: 15000, currency: 'xof', duration_days: 30, description: 'Facturation mensuelle' },
          { id: 'annual', name: 'Annuel', amount: 150000, currency: 'xof', duration_days: 365, description: '2 mois offerts' }
        ]);
      }
    };
    fetchPlans();
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.admin_password !== form.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (form.admin_password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/agencies/register', {
        agency_name: form.agency_name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        city: form.city,
        admin_name: form.admin_name,
        admin_password: form.admin_password,
        plan: form.plan
      });
      setResult(response.data);
      setStep(3);
      toast.success('Agence enregistrée avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f9f4] to-[#e8f5e9] flex items-center justify-center p-4">
      <Toaster position="top-right" />
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-[#1B3A4B] flex items-center justify-center mx-auto mb-3">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">GAB'S SERVICES HABITAT</h1>
          <p className="text-gray-500 mt-1">Plateforme de gestion immobilière</p>
        </div>

        {/* Step Indicators */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3].map(s => (
            <div key={s} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= s ? 'bg-[#1B3A4B] text-white' : 'bg-gray-200 text-gray-500'}`}>
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
          ))}
        </div>

        {/* Step 1: Plan Selection */}
        {step === 1 && (
          <Card>
            <CardHeader><CardTitle>Choisissez votre abonnement</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {plans.map(plan => (
                <div
                  key={plan.id}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${form.plan === plan.id ? 'border-[#1B3A4B] bg-[#1B3A4B]/5' : 'border-gray-200 hover:border-gray-300'}`}
                  onClick={() => setForm(f => ({ ...f, plan: plan.id }))}
                  data-testid={`plan-${plan.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{plan.name}</span>
                        {plan.id === 'annual' && <Badge className="bg-amber-100 text-amber-700 text-xs">Populaire</Badge>}
                      </div>
                      <p className="text-lg font-bold text-[#1B3A4B] mt-1">{plan.amount?.toLocaleString()} FCFA{plan.id === 'monthly' ? '/mois' : '/an'}</p>
                      <p className="text-xs text-gray-500">{plan.description}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 ${form.plan === plan.id ? 'border-[#1B3A4B] bg-[#1B3A4B]' : 'border-gray-300'}`}>
                      {form.plan === plan.id && <Check className="w-3 h-3 text-white m-auto mt-0.5" />}
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-400 text-center mt-2">Accès complet à toutes les fonctionnalités</p>
              <Button onClick={() => setStep(2)} className="w-full bg-[#1B3A4B] hover:bg-[#142D3A] mt-4" data-testid="next-step-btn">
                Continuer <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-center text-sm text-gray-500 mt-3">
                Déjà inscrit ? <Link to="/login" className="text-[#1B3A4B] font-medium hover:underline">Se connecter</Link>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Registration Form */}
        {step === 2 && (
          <Card>
            <CardHeader><CardTitle>Informations de l'agence</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-3">
                <div>
                  <Label className="flex items-center gap-1"><Building2 className="w-3 h-3" /> Nom de l'agence *</Label>
                  <Input value={form.agency_name} onChange={e => setForm(f => ({ ...f, agency_name: e.target.value }))} placeholder="Mon Agence Immobilière" required data-testid="agency-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email *</Label>
                    <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="contact@agence.ml" required data-testid="agency-email" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><Phone className="w-3 h-3" /> Téléphone *</Label>
                    <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+223 XX XX XX XX" required data-testid="agency-phone" />
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Adresse</Label>
                  <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Quartier, Rue..." data-testid="agency-address" />
                </div>
                <hr className="my-2" />
                <div>
                  <Label className="flex items-center gap-1"><User className="w-3 h-3" /> Nom de l'administrateur *</Label>
                  <Input value={form.admin_name} onChange={e => setForm(f => ({ ...f, admin_name: e.target.value }))} placeholder="Nom complet" required data-testid="admin-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="flex items-center gap-1"><Lock className="w-3 h-3" /> Mot de passe *</Label>
                    <Input type="password" value={form.admin_password} onChange={e => setForm(f => ({ ...f, admin_password: e.target.value }))} placeholder="Min. 6 caractères" required data-testid="admin-password" />
                  </div>
                  <div>
                    <Label>Confirmer *</Label>
                    <Input type="password" value={form.confirm_password} onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))} placeholder="Confirmer" required data-testid="confirm-password" />
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">Retour</Button>
                  <Button type="submit" disabled={loading} className="flex-1 bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="submit-register">
                    {loading ? 'Inscription...' : 'S\'inscrire'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Success + Payment Instructions */}
        {step === 3 && result && (
          <Card>
            <CardContent className="p-6">
              <div className="text-center mb-5">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-7 h-7 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Inscription réussie !</h2>
                <p className="text-gray-500 mt-1">{result.agency_name}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2 mb-5">
                <div className="flex justify-between">
                  <span className="text-gray-500">Plan choisi</span>
                  <span className="font-semibold text-gray-900">{result.plan?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Montant</span>
                  <span className="font-bold text-[#1B3A4B] text-base">{result.plan?.amount?.toLocaleString()} FCFA</span>
                </div>
              </div>

              {/* Payment Instructions */}
              <div className="border-2 border-[#F5A623]/30 bg-[#FFF8EE] rounded-xl p-5 mb-5" data-testid="payment-instructions">
                <div className="flex items-center gap-2 mb-3">
                  <Smartphone className="w-5 h-5 text-[#F5A623]" />
                  <h3 className="font-bold text-gray-900">Instructions de paiement</h3>
                </div>
                <p className="text-sm text-gray-700 mb-4">
                  Envoyez le montant correspondant à votre sélection par <strong>Orange Money</strong> ou <strong>Wave</strong> au numéro ci-dessous :
                </p>
                <div
                  className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => { navigator.clipboard.writeText('90123714'); toast.success('Numéro copié !'); }}
                  data-testid="copy-phone-number"
                >
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Numéro de paiement</p>
                    <p className="text-2xl font-bold text-[#1B3A4B] tracking-wider">90 12 37 14</p>
                  </div>
                  <div className="flex items-center gap-1 text-[#1B3A4B] bg-[#1B3A4B]/10 px-3 py-1.5 rounded-lg">
                    <Copy className="w-4 h-4" />
                    <span className="text-xs font-medium">Copier</span>
                  </div>
                </div>
                <div className="mt-4 flex gap-3">
                  <div className="flex-1 bg-[#FF6600]/10 rounded-lg p-2.5 text-center">
                    <p className="text-xs font-bold text-[#FF6600]">Orange Money</p>
                  </div>
                  <div className="flex-1 bg-[#1DC3EC]/10 rounded-lg p-2.5 text-center">
                    <p className="text-xs font-bold text-[#1DC3EC]">Wave</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3 text-center">
                  Votre compte sera activé après validation du paiement par notre équipe.
                </p>
              </div>

              <Button onClick={() => navigate('/login')} className="w-full bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="go-to-login">
                Retour à la connexion
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AgencyRegister;
